import type {ReactNode} from 'react'
import {Children} from 'react'
import {TabList, TabPanel, TabProvider, Tab as AriaTab, useTabStore} from '@ariakit/react'
import {
  addMaybeClassName,
  addMaybeClassNames,
} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Image} from '@freckle/educator-materials/ts/components/img'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {
  tabExpandStyle,
  tabNavsStyle,
  tabContentStyle,
  tabActive,
  tabInactive,
  imgActiveStyle,
  imgInactiveStyle,
  textActiveStyle,
  textInactiveStyle,
} from './tabs.module.scss'

type Props<T extends string> = {
  id: string
  activeKey: T
  addTabNavClass?: string
  children: ReactNode | ReactNode[]
  onSelect: (key: string | null) => void
  expandTabs?: boolean
  addTabContentClass?: string
}

export function Tabs<T extends string>(props: Props<T>) {
  const {id, activeKey, onSelect, children, expandTabs, addTabContentClass, addTabNavClass} = props
  const store = useTabStore({
    selectedId: activeKey,
    setSelectedId: (t: string | null | undefined) => onSelect(t ?? null),
  })

  const childrenAsArray = Children.toArray(children) as {props: TabProps<T>}[]

  return (
    <TabProvider store={store}>
      <TabList className={addMaybeClassName(tabNavsStyle, addTabNavClass)}>
        {childrenAsArray.map(child => {
          const {eventKey, addNavClass, icon, title, navDataTest} = child.props
          return (
            <TabNav
              key={`${id}-nav-${eventKey}`}
              className={addNavClass}
              icon={icon}
              title={title}
              isActive={eventKey === activeKey}
              tabClassName={expandTabs === true ? tabExpandStyle : null}
              dataTest={navDataTest}
              ariaControls={eventKey}
              id={eventKey}
            />
          )
        })}
      </TabList>
      <div className={addMaybeClassName(tabContentStyle, addTabContentClass)}>
        {childrenAsArray.map(child => (
          <TabPanel key={child.props.eventKey}>{child.props.children}</TabPanel>
        ))}
      </div>
    </TabProvider>
  )
}

type TabNavProps = {
  className?: string | null
  icon?: ReactNode
  title: string
  isActive: boolean
  tabClassName?: string | null
  dataTest?: string | null
  ariaControls: string
  id: string
}

function TabNav(props: TabNavProps) {
  const {className, isActive, title, icon, tabClassName, dataTest, ariaControls, id} = props

  const baseClassName = isActive ? tabActive : tabInactive
  const newClassName = addMaybeClassNames(baseClassName, [tabClassName, className])

  const imgClassName = isActive ? imgActiveStyle : imgInactiveStyle
  const iconNode =
    icon === null || icon === undefined ? null : typeof icon === 'string' ? (
      <Image className={imgClassName} src={icon} alt="" title={title} />
    ) : (
      icon
    )

  const textClassName = isActive ? textActiveStyle : textInactiveStyle
  const titleNode = (
    <Text style="subtitle-1" htmlElement="span" className={textClassName}>
      {title}
    </Text>
  )

  return (
    <AriaTab
      aria-selected={isActive}
      aria-controls={ariaControls}
      data-test={dataTest}
      className={newClassName}
      id={id}
    >
      {iconNode}
      {titleNode}
    </AriaTab>
  )
}

/*
 * This component is never used for rendering directly.
 * Instead it is decomposed in the Tabs components and split
 * into TabNav and TabPane which are placed in different
 * section of the TabContainer.
 * We are using this methodology to allow for easily create
 * Tab components.
 * See example of usage:
 *
 * <Tabs activeTab={key} onSelect={handleSelect}>
 *    <Tab title="Home" eventKey="home">
 *      Home content
 *    </Tab>
 *    <Tab title="Profile" eventKey="profile">
 *      Profile content
 *    </Tab>
 *    <Tab title="About" eventKey="about">
 *      About content
 *    </Tab>
 *  </Tabs>
 */

export type TabProps<T> = {
  eventKey: T
  addNavClass?: string | null
  icon?: ReactNode
  title: string
  children: ReactNode
  navDataTest?: string | null
}

export function Tab<T>(_props: TabProps<T>) {
  return <></>
}
